import { platform } from '../billing/checkout'

/**
 * HOW BYTES LEAVE THE APP — the cover-panel viewer's Download, on two platforms.
 *
 * In a browser an `<a download>` on the object URL IS the download. Inside the
 * Capacitor shell it is worse than nothing: WKWebView ignores `download` and
 * NAVIGATES, the bridge's decidePolicyFor sees a blob: URL with no host, hands it
 * to UIApplication.open (which drops it) and answers .cancel — silently (read from
 * the delegate by the appstore fork, 2026-09-13). The owner pressed it on the phone
 * and nothing happened (Download 按鈕在原生的 APP 裡面沒有作用).
 *
 * So in the shell the bytes go out through the platform's own door: the Web Share
 * API with a File — iOS presents its share sheet, where "Save Image" and "Save to
 * Files" are the person's choice, which is also more honest than a silent write
 * into Photos. No plugin, no new binary; the shell loads this bundle live. Should
 * WKWebView refuse files, the fallback is a Filesystem + Share plugin pair, which
 * needs a new build — the appstore fork owns that lane.
 *
 * TRANSIENT USER ACTIVATION is the trap in this door. `navigator.share` may only run
 * while the tap's activation is live, and an `await fetch()` between the tap and the
 * call can spend it — WebKit answers NotAllowedError. So the viewer PREPARES the
 * File when the panel opens (prepareShareFile) and the tap hands it over with no
 * await in front of the share (shareFile). The fetch-then-share composite
 * (saveViaShare) remains for a tap that beat the prepare.
 */

/** Which control the viewer renders: the anchor (browser) or the share button (shell). */
export type SaveLane = 'anchor' | 'share'

export function saveLane(): SaveLane {
  return platform() === 'web' ? 'anchor' : 'share'
}

export type SaveOutcome = 'shared' | 'cancelled' | 'unsupported' | 'not-allowed' | 'failed'

type ShareNavigator = Navigator & {
  canShare?: (data: { files: File[] }) => boolean
  share?: (data: { files: File[]; title?: string }) => Promise<void>
}

/**
 * The bytes at `src`, wrapped as the File the share sheet will be handed.
 *
 * `src` is a URL this page already holds — blob:, data:, or the presigned https of a
 * chat attachment — so the fetch is local or already CORS-cleared. `mime` names the
 * type when the bytes carry none (a data URL and a typed blob already do).
 */
export async function prepareShareFile(src: string, name: string, mime: string): Promise<File> {
  const blob = await (await fetch(src)).blob()
  return new File([blob], name, { type: blob.type || mime })
}

/**
 * Hand a prepared File to the share sheet. NOTHING is awaited before the share
 * itself, so a call from a tap handler runs under that tap's activation.
 */
export function shareFile(file: File, name: string): Promise<SaveOutcome> {
  const nav = navigator as ShareNavigator
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return Promise.resolve('unsupported')
  if (!nav.canShare({ files: [file] })) return Promise.resolve('unsupported')
  return nav.share({ files: [file], title: name }).then(
    () => 'shared' as const,
    (e: unknown) => {
      const err = (e as { name?: string })?.name
      // The sheet dismissed without a choice is not a failure — nothing to say.
      if (err === 'AbortError') return 'cancelled'
      // The tap's activation was gone by the time the sheet was asked for.
      if (err === 'NotAllowedError') return 'not-allowed'
      return 'failed'
    },
  )
}

/** Fetch, then share — for a tap that arrived before the prepare finished. */
export async function saveViaShare(src: string, name: string, mime: string): Promise<SaveOutcome> {
  let file: File
  try {
    file = await prepareShareFile(src, name, mime)
  } catch {
    return 'failed'
  }
  return shareFile(file, name)
}

/** The one sentence for the outcomes a person has to act on. */
export function saveOutcomeNote(outcome: SaveOutcome): string | null {
  if (outcome === 'unsupported') return 'This app build cannot save files yet — update the app, or open this on the web.'
  if (outcome === 'not-allowed') return 'Tap Download once more — the file is ready now.'
  if (outcome === 'failed') return 'Could not hand the file to the share sheet.'
  return null
}
